import { type Map as MapboxMap, Marker } from 'mapbox-gl';
import { type RefObject, useEffect } from 'react';
import { placePinElement } from '../utils/placePin';
import type { PointOfInterestPlace } from '../utils/pointOfInterest';

/**
 * Keeps a pin on a place. The basemap icon fades with the zoom; a DOM marker
 * does not, so the coordinate stays visible. Clearing the place removes the pin.
 * `minZoom` hides it while the map is more zoomed out than that level.
 */
export function useSelectedPlacePin(
  map: RefObject<MapboxMap | null>,
  place: PointOfInterestPlace | null,
  minZoom?: number,
): void {
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !place) return;

    const marker = new Marker({
      element: placePinElement(place),
      anchor: 'bottom',
      occludedOpacity: 1,
    })
      .setLngLat([place.longitude, place.latitude])
      .addTo(currentMap);

    const applyZoom = () => {
      if (minZoom == null) return;
      marker.getElement().style.display = currentMap.getZoom() >= minZoom ? '' : 'none';
    };
    applyZoom();
    if (minZoom != null) currentMap.on('zoom', applyZoom);
    return () => {
      if (minZoom != null) currentMap.off('zoom', applyZoom);
      marker.remove();
    };
  }, [map, place, minZoom]);
}
